import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { MarketTickerCard } from "./MarketTickerCard";
import {
  fetchMarketTickerQuotes,
  formatSignedPct,
  normalizeTickerSymbol,
  type MarketTickerQuote,
} from "./market-ticker-types";
import { FintheonNumericText } from "../../numbers/FintheonNumericText";

const TICKERS = [
  "US02Y",
  "US10Y",
  "US30Y",
  "RTY",
  "VIX",
  "DXY",
  "NQ",
  "ES",
  "YM",
  "GC",
  "CL",
];

const TICKER_PATTERN = new RegExp(
  `(^|[\\s([{>])([/$])?(${TICKERS.join("|")})(?=$|[\\s.,;:!?)}\\]<])`,
  "g",
);

const POPOVER_GAP = 8;
const POPOVER_GUTTER = 10;
const TICKER_CARD_WIDTH = 286;
const TICKER_CARD_ESTIMATED_HEIGHT = 262;

export function enhanceTickerMentions(content: string): string {
  return content
    .split(/(```[\s\S]*?```)/g)
    .map((part) => {
      if (part.startsWith("```")) return part;
      return part.replace(TICKER_PATTERN, (match, prefix, marker, symbol) => {
        const label = `${marker ?? ""}${symbol}`;
        return `${prefix}<market-ticker symbol="${symbol}">${label}</market-ticker>`;
      });
    })
    .join("");
}

export function MarketTickerMention({
  symbol,
  children,
  showPercent = true,
}: {
  symbol?: string;
  children?: ReactNode;
  showPercent?: boolean;
}) {
  const normalized = normalizeTickerSymbol(symbol);
  const [quote, setQuote] = useState<MarketTickerQuote | null>(null);
  const [open, setOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (quote || !normalized) return;
    let cancelled = false;
    fetchMarketTickerQuotes().then((quotes) => {
      if (cancelled) return;
      setQuote(
        quotes.find((item) => item.label.toUpperCase() === normalized) ?? null,
      );
    });
    return () => {
      cancelled = true;
    };
  }, [normalized, quote]);

  const direction = useMemo(() => {
    if (!quote) return "flat";
    return quote.change >= 0 ? "up" : "down";
  }, [quote]);

  function showCard() {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    setIsClosing(false);
    updatePosition();
    setOpen(true);
  }

  function updatePosition() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const popoverRect = popoverRef.current?.getBoundingClientRect();
    const width = popoverRect?.width || TICKER_CARD_WIDTH;
    const height = popoverRect?.height || TICKER_CARD_ESTIMATED_HEIGHT;
    const maxLeft = Math.max(
      POPOVER_GUTTER,
      window.innerWidth - width - POPOVER_GUTTER,
    );
    const maxTop = Math.max(
      POPOVER_GUTTER,
      window.innerHeight - height - POPOVER_GUTTER,
    );
    const preferredLeft = rect.left - width - POPOVER_GAP;
    const preferredTop = rect.top - height - POPOVER_GAP;

    setPosition({
      left: Math.min(maxLeft, Math.max(POPOVER_GUTTER, preferredLeft)),
      top: Math.min(maxTop, Math.max(POPOVER_GUTTER, preferredTop)),
    });
  }

  function scheduleClose() {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setIsClosing(true);
      closeTimerRef.current = window.setTimeout(() => {
        setOpen(false);
        setIsClosing(false);
      }, 140);
    }, 120);
  }

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, quote]);

  useEffect(() => {
    if (!open) return;
    const handleViewportChange = () => updatePosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, quote]);

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex align-baseline"
      onMouseEnter={showCard}
      onMouseLeave={scheduleClose}
      onFocus={showCard}
      onBlur={scheduleClose}
    >
      <button
        type="button"
        className="fintheon-inline-ticker"
        data-direction={direction}
      >
        <span>{children}</span>
        {quote && showPercent ? (
          <span>
            <FintheonNumericText
              text={formatSignedPct(quote.changePercent)}
              kind="percentage-change"
            />
          </span>
        ) : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="fintheon-market-ticker-card-popover fixed z-[1000]"
              style={{ left: position.left, top: position.top }}
              onMouseEnter={showCard}
              onMouseLeave={scheduleClose}
            >
              {quote ? (
                <MarketTickerCard quote={quote} isClosing={isClosing} />
              ) : (
                <span
                  className={`fintheon-popover-surface fintheon-popover-motion block w-[220px] p-3 text-[11px] text-[var(--fintheon-muted)] ${
                    isClosing ? "is-closing" : ""
                  }`}
                >
                  Loading {normalized}...
                </span>
              )}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
